/**
 * Curricula a teacher can align to from Align → Curriculum.
 *
 * All of them are listed in the published `curricula.json` index, which
 * diffit-standards' `curriculum-index` command builds: IM® v.360 from the
 * Learning Commons knowledge graph, and the rest from IXL's public skill plans
 * or from one customer. Ids must pass `is_supported_curriculum_id` in
 * diffit-api/app/constants/curriculum.py.
 */

/** Subjects in the order the curriculum menu groups them. */
export const CURRICULUM_SUBJECTS = ['Math', 'ELA', 'Science', 'Social studies'];

let registered = [];

/**
 * Map the published index onto the picker's curriculum entries.
 *
 * @param {Object[]} entries - `curricula` from curricula.json
 */
export const toCurricula = (entries) =>
  (entries || [])
    .filter((entry) => entry?.id && entry.name)
    .map((entry) => ({
      id: entry.id,
      subject: entry.subject,
      // `name` reaches the generation prompt as curriculum_name; `displayName` may carry a ®.
      name: entry.name,
      displayName: entry.display_name || entry.name,
      description: [entry.grades, entry.subject].filter(Boolean).join(' '),
      publisher: entry.publisher,
      // learning_commons, ixl, or customer — sent with the Mixpanel events.
      source: entry.source,
      // IM v.360 is published as public but locked like the IXL curricula.
      isRestricted: entry.access === 'restricted' || entry.source === 'learning_commons',
      attribution: entry.attribution,
      license: entry.license || null,
    }));

/** Make the published curricula available to `getCurriculum` and `allCurricula`. */
export const registerCurricula = (curricula) => {
  registered = curricula;
};

export const allCurricula = () => registered;

/**
 * Restricted curricula every teacher sees locked, so they can ask their admin to
 * enable one. Customer curricula stay hidden: they are one school's own.
 */
const isRequestable = (curriculum) =>
  ['ixl', 'learning_commons'].includes(curriculum.source) && curriculum.subject === 'Math';

/**
 * The curricula a teacher may see: every public one, the restricted ones the
 * `extra-curricula` flag names for them, and then the requestable rest, marked
 * `isLocked`.
 *
 * @param {Object[]} curricula
 * @param {string[]} [extraIds] - the flag's value
 */
export const visibleCurricula = (curricula, extraIds) => {
  const extra = new Set(Array.isArray(extraIds) ? extraIds : []);
  const enabled = curricula.filter(
    (curriculum) => !curriculum.isRestricted || extra.has(curriculum.id)
  );
  const locked = curricula
    .filter(
      (curriculum) =>
        curriculum.isRestricted && !extra.has(curriculum.id) && isRequestable(curriculum)
    )
    .map((curriculum) => ({ ...curriculum, isLocked: true }));
  return [...enabled, ...locked];
};

/**
 * Curricula whose name, subject, grades, or publisher contain every word of the query.
 */
export const searchCurricula = (curricula, query) => {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return curricula;
  return curricula.filter((curriculum) => {
    const text = [curriculum.displayName, curriculum.description, curriculum.publisher]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return words.every((word) => text.includes(word));
  });
};

/**
 * Where a teacher goes when the curriculum they teach isn't listed yet.
 *
 * Mirrors the "Request more standards" link in the standards modal. Prefilled
 * from the profile so a teacher isn't retyping what we already know; fields we
 * can't fill are left out entirely, so the form shows its own empty input
 * rather than a literal placeholder.
 */
export const CURRICULUM_REQUEST_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSch1y6ATcqWoo5jHBs4iHm8Wsr3cAX79Bv5ARcA9NkZ2u5TFA/viewform';

// Prefill keys from the form's own pp_url link.
const REQUEST_FORM_ENTRIES = {
  name: 'entry.1027839856',
  email: 'entry.1636186709',
  school: 'entry.1450306026',
  schoolId: 'entry.2024620762',
};

/**
 * Build the request-form URL, prefilled with whatever the profile knows.
 *
 * @param {Object} [sources]
 * @param {Object} [sources.user] - Clerk user (useUser)
 * @param {Object} [sources.diffitUser] - Diffit profile
 * @param {Object} [sources.selectedOrg] - the active org_user_info (org_id + nested org)
 * @returns {string} a viewform URL carrying only the fields we could fill
 */
export const buildCurriculumRequestUrl = ({ user, diffitUser, selectedOrg } = {}) => {
  const name =
    [diffitUser?.given_name || user?.firstName, diffitUser?.family_name || user?.lastName]
      .filter(Boolean)
      .join(' ') ||
    user?.fullName ||
    '';

  // Name and id come from the same record, so the two fields can never describe
  // different schools.
  const school = diffitUser?.school_records?.[0];

  const prefill = {
    [REQUEST_FORM_ENTRIES.name]: name,
    [REQUEST_FORM_ENTRIES.email]:
      diffitUser?.email || user?.primaryEmailAddress?.emailAddress || '',
    // A school record is the more specific answer to "School/District Name"; the
    // org name is the fallback for district-licensed teachers, and brings no
    // school id with it.
    [REQUEST_FORM_ENTRIES.school]: school?.name || selectedOrg?.org?.name || '',
    [REQUEST_FORM_ENTRIES.schoolId]: school?.id || '',
  };

  const params = new URLSearchParams({ usp: 'pp_url' });
  Object.entries(prefill).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  return `${CURRICULUM_REQUEST_FORM_URL}?${params.toString()}`;
};

/** How many curriculum lessons a teacher may align one resource to. */
export const MAX_CURRICULUM_LESSONS = 5;

export const getCurriculum = (curriculumId) =>
  allCurricula().find((c) => c.id === curriculumId) || null;

/**
 * Map a published course file's units onto the rows the picker renders.
 *
 * The unit → section → lesson walk lives in the publishing tool
 * (diffit-standards `standards_tool/sources/learning_commons.py`), so a course
 * file arrives as units holding their lessons directly, each lesson carrying
 * its section name and the standards it teaches.
 *
 * @param {Object} course - a published course file
 * @returns {Array} [{ id, label, lessons: [{ id, name, ordinalName, sectionName, standards }] }]
 */
export const flattenUnits = (course) =>
  (course?.units || []).map((unit) => ({
    id: unit.id,
    label: unit.label,
    lessons: (unit.lessons || []).map((lesson) => ({
      id: lesson.id,
      name: lesson.name,
      ordinalName: lesson.ordinal_name,
      sectionName: lesson.section_name,
      isOptional: lesson.is_optional,
      standards: lesson.standards || [],
      // Not rendered; sent with the lesson so the create prompt knows what it practices.
      skills: lesson.skills || [],
      unitName: unit.label,
    })),
  }));

/** "Lesson 5: Compare Decimals" — the lesson label used in chips and the prompt. */
export const lessonLabel = (lesson) =>
  [lesson?.ordinalName, lesson?.name].filter(Boolean).join(': ') || lesson?.id || '';

/**
 * Context describing the selected curriculum, for the follow-up-question generator.
 *
 * Label composition mirrors `_curriculum_template_data` in
 * diffit-api/app/strategies/generate/create_streaming/create_base.py so the
 * follow-up prompt and the create prompt name the same lesson the same way.
 *
 * @param {Object|null} selection - { curriculumName, courseName, lessons }
 * @returns {Object} `{}` when nothing is selected, so it spreads into a context object
 */
export const buildCurriculumContext = (selection) => {
  if (!selection?.lessons?.length) return {};
  return {
    has_curriculum: true,
    curriculum_name: selection.curriculumName,
    curriculum_course_name: selection.courseName,
    curriculum_lessons: selection.lessons.map((lesson) => ({
      label: lesson.unitName ? `${lesson.unitName} — ${lessonLabel(lesson)}` : lessonLabel(lesson),
      ...(lesson.skills?.length ? { skills: lesson.skills } : {}),
    })),
  };
};

/**
 * The standards the selected lessons teach, deduped by code.
 *
 * These are published alongside each lesson, so they travel to the backend in
 * `content_standards_json` exactly like the standards picker's own selections —
 * the create prompt's Standards Alignment section then covers them with no
 * special case.
 */
export const curriculumStandards = (selection) => {
  const byId = new Map();
  (selection?.lessons || []).forEach((lesson) =>
    (lesson.standards || []).forEach((standard) => {
      if (standard?.id && !byId.has(standard.id)) byId.set(standard.id, standard);
    })
  );
  return [...byId.values()];
};

/**
 * Merge the curriculum's standards into the teacher's own content standards.
 *
 * A code can be reached both ways — picked by hand in the standards picker and
 * taught by a selected lesson. The teacher's entry wins, because only that one
 * can carry a `focus` note, and the prompt renders `focus` per standard.
 *
 * @param {Object[]} contentStandards - standards picked in the standards modal
 * @param {Object|null} selection - the Align → Curriculum selection
 * @returns {Object[]} the union, teacher's entries first
 */
export const mergeCurriculumStandards = (contentStandards, selection) => {
  const picked = contentStandards || [];
  return [
    ...picked,
    ...curriculumStandards(selection).filter(
      (standard) => !picked.some((entry) => entry.id === standard.id)
    ),
  ];
};

/**
 * Standards and curriculum context for the follow-up-question generator.
 *
 * The selected lessons' standards join the teacher's picks, as they do on the
 * create path, so a curriculum-only selection still reaches the prompt's
 * Selected Standards section.
 *
 * @returns {Object} `{}` when nothing is selected, so it spreads into a context object
 */
export const followUpAlignmentContext = ({
  languageStandards,
  contentStandards,
  curriculumSelection,
}) => {
  const content = mergeCurriculumStandards(contentStandards, curriculumSelection);
  return {
    ...(languageStandards.length > 0 || content.length > 0
      ? { has_standards: true, language_standards: languageStandards, content_standards: content }
      : {}),
    ...buildCurriculumContext(curriculumSelection),
  };
};

/**
 * Build the `curriculum_json` create param: the selected lessons' identity.
 *
 * Only ids, labels, and the skills a lesson practices go over the wire. The
 * standards those lessons teach are sent separately via `curriculumStandards` in
 * `content_standards_json`, and the backend validates this payload as identity
 * only — it resolves no standards of its own, so nothing arrives twice.
 */
export const buildCurriculumPayload = (selection) => {
  if (!selection?.lessons?.length) return undefined;
  const curriculum = getCurriculum(selection.curriculumId);
  return {
    curriculum_id: selection.curriculumId,
    curriculum_name: curriculum?.name || selection.curriculumName,
    course_id: selection.courseId,
    course_name: selection.courseName,
    lessons: selection.lessons.map((lesson) => ({
      id: lesson.id,
      name: lesson.name,
      ordinal_name: lesson.ordinalName,
      unit_name: lesson.unitName,
      ...(lesson.skills?.length ? { skills: lesson.skills } : {}),
    })),
  };
};
